import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useRouter } from '@tanstack/react-router';
import { CheckCircle2 } from 'lucide-react';
import { AlertBanner, Button, FieldError, HelperText, Input, Label } from '@/components/ui';
import { AuthShell } from '@/components/AuthShell';
import { partnerApi } from '@/lib/api-affiliate';
import { toast } from 'sonner';

const schema = z
  .object({
    password: z.string().min(8, 'Минимум 8 символов'),
    passwordConfirm: z.string(),
  })
  .refine((d) => d.password === d.passwordConfirm, {
    path: ['passwordConfirm'],
    message: 'Пароли не совпадают',
  });
type FormValues = z.infer<typeof schema>;

function scorePassword(p: string): { score: 0 | 1 | 2 | 3 | 4; label: string } {
  if (!p) return { score: 0, label: '' };
  let s = 0;
  if (p.length >= 8) s++;
  if (p.length >= 12) s++;
  if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s++;
  if (/\d/.test(p) && /[^A-Za-z0-9]/.test(p)) s++;
  const score = Math.min(4, s) as 0 | 1 | 2 | 3 | 4;
  const labels = ['', 'Слабый', 'Средний', 'Хороший', 'Отличный'];
  return { score, label: labels[score] };
}

function StrengthMeter({ value }: { value: string }) {
  const { score, label } = scorePassword(value);
  if (!value) return null;
  const colorVar =
    score <= 1
      ? 'var(--danger)'
      : score === 2
      ? 'var(--warning)'
      : score === 3
      ? 'var(--info)'
      : 'var(--success)';
  return (
    <div className="mt-2">
      <div className="flex gap-1">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="h-1 flex-1 rounded-full transition-colors"
            style={{
              background: i <= score ? colorVar : 'var(--bg-elevated)',
            }}
          />
        ))}
      </div>
      <p className="text-[11px] mt-1 font-medium" style={{ color: colorVar }}>
        {label}
      </p>
    </div>
  );
}

export function ResetPasswordConfirmPage() {
  const router = useRouter();
  const [token, setToken] = useState<string>('');
  const [done, setDone] = useState(false);
  const reset = partnerApi.auth.useResetConfirm();

  useEffect(() => {
    const hash = window.location.hash; // "#/reset-password/confirm?token=…"
    const qIdx = hash.indexOf('?');
    if (qIdx >= 0) {
      const sp = new URLSearchParams(hash.slice(qIdx + 1));
      setToken(sp.get('token') ?? '');
    }
  }, []);

  const { register, handleSubmit, watch, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });
  const passwordValue = watch('password') ?? '';

  const onSubmit = async (v: FormValues) => {
    if (!token) {
      toast.error('Токен отсутствует или недействителен');
      return;
    }
    await reset.mutateAsync({ token, password: v.password });
    toast.success('Пароль обновлён');
    setDone(true);
    setTimeout(() => router.navigate({ to: '/login' }), 1500);
  };

  if (done) {
    return (
      <AuthShell>
        <div className="flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-full bg-[var(--success-soft)] grid place-items-center text-[var(--success)] mb-4">
            <CheckCircle2 size={26} />
          </div>
          <h1 className="text-[22px] font-semibold mb-2 text-[var(--text)] tracking-tight">
            Пароль обновлён
          </h1>
          <p className="text-sm text-[var(--text-muted)]">Сейчас перенаправим к входу…</p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Новый пароль"
      subtitle="Введите новый пароль для аккаунта"
      footer={
        <Link to="/login" className="text-[var(--text-muted)] hover:text-[var(--text)]">
          ← Назад ко входу
        </Link>
      }
    >
      {!token && (
        <AlertBanner tone="danger" className="mb-4">
          Токен сброса не найден. Запросите сброс заново.
        </AlertBanner>
      )}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <Label htmlFor="password">Пароль</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            {...register('password')}
          />
          <FieldError message={formState.errors.password?.message} />
          <StrengthMeter value={passwordValue} />
          {!passwordValue && (
            <HelperText>Минимум 8 символов. Используйте буквы, цифры и спецсимволы.</HelperText>
          )}
        </div>
        <div>
          <Label htmlFor="passwordConfirm">Повторите</Label>
          <Input
            id="passwordConfirm"
            type="password"
            autoComplete="new-password"
            {...register('passwordConfirm')}
          />
          <FieldError message={formState.errors.passwordConfirm?.message} />
        </div>
        <Button
          type="submit"
          loading={reset.isPending}
          disabled={!token}
          className="w-full"
          variant="gradient"
          size="lg"
        >
          {reset.isPending ? 'Сохранение…' : 'Установить пароль'}
        </Button>
      </form>
    </AuthShell>
  );
}
